import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import qs from 'qs';

// 発注APIベースURI
const resource = '/purchases';
/**
 * 発注関連API呼出しクラス
 */
export default class PurchaseRepository {

    constructor(private repository: AxiosInstance) { }

    /**
     * 発注情報取得
     * @param id - 発注ID
     * @return 発注情報
     */
    public async getPurchaseInfo(param: PurchaseType.PurchaseParam): Promise<PurchaseType.PurchasePagenate> {

        // パラメータ設定
        const apiParams: AxiosRequestConfig = {
            params: param,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' })
            }
        };
        // API呼出し
        const response = await this.repository.get<PurchaseType.PurchasePagenate>(`${resource}`, apiParams);
        const purchase = response.data;
        return purchase;

    }

    /**
     * 発注詳細情報取得
     * @param id - 発注ID
     * @return 発注詳細情報
     */
    public async getPurchaseDetailInfo(purchaseId: number): Promise<PurchaseType.PurchaseDetail> {

        // API呼出し
        const response = await this.repository.get<PurchaseType.PurchaseDetail>(`${resource}/${purchaseId}`);
        return response.data;

    }

    /**
     * 発注確認処理
     * @param id - 発注ID
     * @return 処理結果
     */
    public async execConfirmation(purchaseId: number, rowVersion: string): Promise<boolean> {

        // API呼出し
        const response = await this.repository.post<number>(`${resource}/${purchaseId}/confirmation`, { RowVersion: rowVersion });
        return true;

    }

    /**
     * 構成データ添付処理
     * @param id - 発注ID
     * @return 処理結果
     */
    public async execConstitutionData(purchaseId: number, rowVersion: string, purchaseAttachment: PurchaseType.PurchaseAttachment[]): Promise<boolean> {

        // API呼出し
        const response = await this.repository.post<number>(`${resource}/${purchaseId}/constitution-data`, { RowVersion: rowVersion, PurchaseAttachment: purchaseAttachment });
        return true;

    }

    /**
     * 納品処理
     * @param id - 発注ID
     * @return 処理結果
     */
    public async execDelivery(purchaseId: number, rowVersion: string, purchaseGoods: PurchaseType.DeliveryPurchaseGoods[], purchaseAttachment: PurchaseType.PurchaseAttachment[]): Promise<boolean> {

        // API呼出し
        const response = await this.repository.post<number>(`${resource}/${purchaseId}/delivery`, { RowVersion: rowVersion, PurchaseGoods: purchaseGoods, PurchaseAttachment: purchaseAttachment });
        return true;

    }

    /**
     * 請求登録処理
     * @param id - 発注ID
     * @return 処理結果
     */
    public async execInvoice(purchaseId: number, rowVersion: string, authCode: string): Promise<boolean> {

        // API呼出し
        const response = await this.repository.post<number>(`${resource}/${purchaseId}/invoice`, { RowVersion: rowVersion, AuthCode: authCode });
        return true;

    }

    /**
     * 請求登録処理(複数件)
     * @param idList - 発注ID、行番号のリスト
     * @return 処理結果（成功時、結果件数 ： 失敗時、エラーコード）
     */
    public async execInvoices(purchaseIdList: PurchaseType.PurchaseIdList[], authCode: string): Promise<PurchaseType.InvoicesResult> {

        // API呼出し
        const response = await this.repository.post<PurchaseType.InvoicesResult>(`${resource}/invoice`, { purchaseIdList: purchaseIdList, AuthCode: authCode });
        return response.data;

    }

    /**
     * 新規登録処理
     * @param id - 発注ID
     * @return 処理結果
     */
    public async execRegist(purchaseinfo: PurchaseType.RegistPurchaseDetail): Promise<boolean> {

        // API呼出し
        const response = await this.repository.post<number>(`${resource}/new-regist`, purchaseinfo);
        return true;

    }

    /**
     * 請求書取得処理
     * @param purchaseOrderIds - 発注ID配列
     * @return 処理結果
     */
    public async getInvoicePdf(purchaseOrderIds: number[]): Promise<File> {

        const headers = { "Content-type": "application/octet-stream" };

        // パラメータ設定
        const apiParams: AxiosRequestConfig = {
            headers: headers,
            responseType: 'blob',
            params: { purchaseOrderId: purchaseOrderIds },
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' })
            }
        };
        // API呼出し
        const response = await this.repository.get<File>(`${resource}/invoice-pdf`, apiParams);
        return response.data;
    }

    /**
     * 請求書取得処理
     * @param purchaseOrderIds - 発注ID配列
     * @return 処理結果
     */
    public async getInvoiceCsv(purchaseOrderIds: number[]): Promise<File> {

        const headers = { "Content-type": "application/octet-stream" };

        // パラメータ設定
        const apiParams: AxiosRequestConfig = {
            headers: headers,
            responseType: 'blob',
            params: { purchaseOrderId: purchaseOrderIds },
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' })
            }
        };
        // API呼出し
        const response = await this.repository.get<File>(`${resource}/invoice-csv`, apiParams);
        return response.data;
    }

    /**
     * 月次請求書取得処理
     * @param targetMonth - 対象年月
     * @return 処理結果
     */
    public async getMonthlyInvoicePdf(targetMonth: string): Promise<File> {

        const headers = { "Content-type": "application/octet-stream" };

        // パラメータ設定
        const apiParams: AxiosRequestConfig = {
            headers: headers,
            responseType: 'blob',
            params: { targetMonth },
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' })
            }
        };
        // API呼出し
        const response = await this.repository.get<File>(`${resource}/monthly-invoice-pdf`, apiParams);
        return response.data;
    }

    /**
     * 月次請求書(CSV)取得処理
     * @param targetMonth - 対象年月
     * @return 処理結果
     */
    public async getMonthlyInvoiceCsv(targetMonth: string): Promise<File> {

        const headers = { "Content-type": "application/octet-stream" };

        // パラメータ設定
        const apiParams: AxiosRequestConfig = {
            headers: headers,
            responseType: 'blob',
            params: { targetMonth },
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' })
            }
        };
        // API呼出し
        const response = await this.repository.get<File>(`${resource}/monthly-invoice-csv`, apiParams);
        return response.data;
    }

    /**
     * 請求書発行用年月情報取得
     * @return 請求書発行用年月情報
     */
    public async getInvoiceYearMonth(): Promise<PurchaseType.InvoiceYearMonth[]> {

        // API呼出し
        const response = await this.repository.get<PurchaseType.InvoiceYearMonth[]>(`${resource}/invoice/year-month`);
        return response.data;
    }
}